import type {
  ApiListResponse,
  CardPayload,
  Channel,
  DraftItem,
  HistoryItem,
  MeResponse,
  ScheduledItem,
  SendResult,
  TemplateItem,
  User
} from "./types";

export class ApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function parseResponse<T>(response: Response): Promise<T> {
  const contentType = response.headers.get("content-type") ?? "";
  const data = (contentType.includes("application/json") ? await response.json() : null) as { error?: string } | null;
  if (!response.ok) {
    throw new ApiError(data?.error ?? `Request failed (${response.status})`, response.status);
  }
  return data as T;
}

async function request<T>(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  if (init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(path, {
    ...init,
    headers,
    credentials: "include"
  });
  return parseResponse<T>(response);
}

export const api = {
  me: () => request<MeResponse>("/api/auth/me"),
  setupOwner: (body: { setupSecret: string; username: string; displayName: string; password: string }) =>
    request<{ user: User }>("/api/auth/setup-owner", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  login: (body: { username: string; password: string }) =>
    request<{ user: User }>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify(body)
    }),
  logout: () => request<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  users: () => request<ApiListResponse<User>>("/api/users"),
  createUser: (body: { username: string; displayName: string; password: string; role: string }) =>
    request<{ user: User }>("/api/users", { method: "POST", body: JSON.stringify(body) }),
  updateUser: (id: string, body: Partial<Pick<User, "displayName" | "role" | "active">>) =>
    request<{ user: User }>(`/api/users/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  resetPassword: (id: string, password: string) =>
    request<{ user: User }>(`/api/users/${id}/reset-password`, { method: "POST", body: JSON.stringify({ password }) }),
  disableUser: (id: string) => request<{ user: User }>(`/api/users/${id}/disable`, { method: "POST" }),
  deleteUser: (id: string) => request<{ ok: true }>(`/api/users/${id}`, { method: "DELETE" }),

  channels: () => request<ApiListResponse<Channel>>("/api/channels"),
  createChannel: (body: Pick<Channel, "name" | "telegramId" | "targetType" | "active" | "isDefault">) =>
    request<{ channel: Channel }>("/api/channels", { method: "POST", body: JSON.stringify(body) }),
  updateChannel: (id: string, body: Partial<Pick<Channel, "name" | "telegramId" | "targetType" | "active" | "isDefault">>) =>
    request<{ channel: Channel }>(`/api/channels/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  deleteChannel: (id: string) => request<{ ok: true }>(`/api/channels/${id}`, { method: "DELETE" }),

  sendNow: (payload: CardPayload, media: File) => {
    const formData = new FormData();
    formData.append("payload", JSON.stringify(payload));
    formData.append("media", media);
    return request<SendResult>("/api/cards/send-now", {
      method: "POST",
      body: formData
    });
  },

  scheduleCard: (payload: CardPayload, media: File, scheduledAt: string, timezone = "Asia/Bangkok") => {
    const formData = new FormData();
    formData.append("payload", JSON.stringify(payload));
    formData.append("media", media);
    formData.append("scheduledAt", scheduledAt);
    formData.append("timezone", timezone);
    return request<{ scheduled: ScheduledItem }>("/api/cards/schedule", {
      method: "POST",
      body: formData
    });
  },

  drafts: () => request<ApiListResponse<DraftItem>>("/api/drafts"),
  saveDraft: (body: { name: string; payload: CardPayload }) =>
    request<{ draft: DraftItem }>("/api/drafts", { method: "POST", body: JSON.stringify(body) }),
  deleteDraft: (id: string) => request<{ ok: true }>(`/api/drafts/${id}`, { method: "DELETE" }),

  templates: () => request<ApiListResponse<TemplateItem>>("/api/templates"),
  saveTemplate: (body: { name: string; payload: CardPayload }) =>
    request<{ template: TemplateItem }>("/api/templates", { method: "POST", body: JSON.stringify(body) }),
  deleteTemplate: (id: string) => request<{ ok: true }>(`/api/templates/${id}`, { method: "DELETE" }),

  scheduled: () => request<ApiListResponse<ScheduledItem>>("/api/scheduled"),
  processScheduledDue: () => request<{ processed: number }>("/api/scheduled/process-due", { method: "POST" }),
  cancelScheduled: (id: string) => request<{ scheduled: ScheduledItem }>(`/api/scheduled/${id}`, { method: "DELETE" }),
  deleteScheduled: (id: string) => request<{ ok: true }>(`/api/scheduled/${id}`, { method: "DELETE" }),
  clearCompletedScheduled: () => request<{ cleared: number }>("/api/scheduled/completed", { method: "DELETE" }),
  history: () => request<ApiListResponse<HistoryItem>>("/api/history"),
  deleteHistory: (id: string) => request<{ ok: true }>(`/api/history/${id}`, { method: "DELETE" }),
  clearHistory: () => request<{ ok: true }>("/api/history", { method: "DELETE" })
};
